import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, RefreshCw, Trash2, CheckCircle, Clock, Users as UsersIcon, AlertTriangle, Download, ExternalLink, Plus, ArrowLeft, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Entwurf', cls: 'bg-gray-100 text-gray-700' },
  { value: 'live', label: 'Live', cls: 'bg-green-100 text-green-800' },
  { value: 'sold_out', label: 'Ausgebucht', cls: 'bg-orange-100 text-orange-800' },
  { value: 'past', label: 'Vorbei', cls: 'bg-gray-200 text-gray-500' },
];

const SIGNUP_STATUS_OPTIONS = [
  { value: 'new', label: 'Neu', cls: 'bg-blue-100 text-blue-800' },
  { value: 'confirmed', label: 'Bestätigt', cls: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
  { value: 'cancelled', label: 'Storniert', cls: 'bg-gray-200 text-gray-500' },
];

function toDateTimeLocal(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function StatusPill({ value, options }) {
  const opt = options.find((o) => o.value === value) || { label: value || '—', cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${opt.cls}`}>{opt.label}</span>
  );
}

function formatGermanDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

function exportSignupsCSV(signups, label) {
  const header = 'Eingang;Status;Anwesend;Name;Email;Unternehmen;Funktion;UTM-Source;UTM-Medium;Referrer;Nachricht\n';
  const rows = signups.map((s) => {
    const safe = (v) => `"${String(v || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    return [
      new Date(s.created_at).toLocaleString('de-DE'),
      s.status,
      s.attended ? 'ja' : 'nein',
      safe(s.name), safe(s.email), safe(s.company), safe(s.role),
      safe(s.utm_source), safe(s.utm_medium), safe(s.referrer_path),
      safe(s.message),
    ].join(';');
  }).join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `masterclass-signups-${label || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ────────────────────────────────────────────────────────────────
// Hauptkomponente
// ────────────────────────────────────────────────────────────────
export default function MasterclassAdmin() {
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [activeEventId, setActiveEventId] = useState(null);

  const openDetail = (id) => { setActiveEventId(id); setView('detail'); };
  const backToList = () => { setActiveEventId(null); setView('list'); };

  if (view === 'detail' && activeEventId) {
    return <EventDetail eventId={activeEventId} onBack={backToList} />;
  }
  return <EventsList onOpen={openDetail} />;
}

// ────────────────────────────────────────────────────────────────
// Events-Liste
// ────────────────────────────────────────────────────────────────
function EventsList({ onOpen }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/masterclass/events`, { headers: authHeaders() });
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createEvent = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/admin/masterclass/events`, {
        title: 'KI-Masterclass für Betreiber & Entscheider',
        status: 'draft',
        max_seats: 100,
        signup_open: true,
      }, { headers: authHeaders() });
      if (r.data?.id) onOpen(r.data.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
    setCreating(false);
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Lade Events…</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1280px] mx-auto">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1f1f1f] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            KI-Masterclass · Events
          </h1>
          <p className="text-sm text-gray-500">Alle 4–6 Wochen ein neues Event. KPIs werden pro Event und aggregiert über alle Events erfasst.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50">
            <RefreshCw size={14} /> Neu laden
          </button>
          <button onClick={createEvent} disabled={creating}
            className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#befa0a] hover:text-[#0a0a0a]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={14} /> {creating ? 'Erstelle…' : 'Neues Event'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">
          <AlertTriangle size={14} className="inline mr-2" /> {error}
        </div>
      )}

      {events.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-12 text-center text-gray-400">
          <Calendar size={36} className="mx-auto mb-3 opacity-40" />
          <p className="mb-4">Noch kein Event angelegt. Erstelle dein erstes — Termin, Preis und Anmelde-Formular kannst du danach füllen.</p>
          <button onClick={createEvent} disabled={creating}
            className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-5 py-2.5 text-xs uppercase tracking-wider font-bold hover:bg-[#befa0a] hover:text-[#0a0a0a]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={14} /> Erstes Event anlegen
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Termin</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Anmeldungen</th>
                <th className="px-4 py-2.5">Aktiv</th>
                <th className="px-4 py-2.5">Anwesend</th>
                <th className="px-4 py-2.5">Show-up</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.map((e) => {
                const showUp = e.signups_count > 0 ? Math.round(100 * (e.attended_count || 0) / e.signups_count) : 0;
                return (
                  <tr key={e.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onOpen(e.id)}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#1f1f1f]">{e.date_label || formatGermanDate(e.event_at) || 'Termin offen'}</div>
                      <div className="text-xs text-gray-500">{e.format_label || '—'}</div>
                    </td>
                    <td className="px-4 py-3"><StatusPill value={e.status} options={STATUS_OPTIONS} /></td>
                    <td className="px-4 py-3 font-medium">{e.signups_count ?? 0}</td>
                    <td className="px-4 py-3 text-[#658a07]">{e.signups_active ?? 0}</td>
                    <td className="px-4 py-3">{e.attended_count ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{e.signups_count > 0 ? `${showUp}%` : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-[#658a07] text-xs uppercase tracking-wider font-bold">Öffnen →</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Event-Detail (Settings + Signups)
// ────────────────────────────────────────────────────────────────
function EventDetail({ eventId, onBack }) {
  const [event, setEvent] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [e, sg] = await Promise.all([
        axios.get(`${API}/admin/masterclass/events/${eventId}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/masterclass/events/${eventId}/signups`, { headers: authHeaders() }),
      ]);
      setEvent(e.data || null);
      setSignups(Array.isArray(sg.data) ? sg.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const updateField = (field, value) => setEvent((e) => ({ ...e, [field]: value }));

  const save = async () => {
    if (!event) return;
    setSaving(true); setError('');
    try {
      const payload = { ...event };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const r = await axios.patch(`${API}/admin/masterclass/events/${eventId}`, payload, { headers: authHeaders() });
      setEvent(r.data || event);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const deleteEvent = async () => {
    if (!window.confirm('Event wirklich löschen? Signups bleiben erhalten (event_id wird auf NULL gesetzt).')) return;
    try {
      await axios.delete(`${API}/admin/masterclass/events/${eventId}`, { headers: authHeaders() });
      onBack();
    } catch (err) {
      alert(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const updateSignupField = async (id, field, value) => {
    try {
      await axios.patch(`${API}/admin/masterclass/signups/${id}`, { [field]: value }, { headers: authHeaders() });
      setSignups((arr) => arr.map((s) => (s.id === id ? { ...s, [field]: value, ...(field === 'attended' && value ? { attended_at: new Date().toISOString() } : {}) } : s)));
    } catch (err) {
      alert(err.response?.data?.detail || 'Update fehlgeschlagen');
    }
  };

  const deleteSignup = async (id) => {
    if (!window.confirm('Anmeldung wirklich löschen? (DSGVO-Anfrage o.ä.)')) return;
    try {
      await axios.delete(`${API}/admin/masterclass/signups/${id}`, { headers: authHeaders() });
      setSignups((arr) => arr.filter((s) => s.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Lade Event…</div>;
  if (!event) return (
    <div className="p-8">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#1f1f1f] mb-4 inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Zurück</button>
      <div className="bg-red-50 border border-red-200 text-red-700 p-4">Event nicht ladbar. {error}</div>
    </div>
  );

  const active = signups.filter((s) => s.status === 'new' || s.status === 'confirmed').length;
  const attended = signups.filter((s) => s.attended).length;
  const freeSeats = Math.max(0, (event.max_seats || 0) - active);
  const showUp = signups.length > 0 ? Math.round(100 * attended / signups.length) : 0;

  return (
    <div className="p-6 md:p-8 max-w-[1280px] mx-auto">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <button onClick={onBack} className="text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f] mb-3 inline-flex items-center gap-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            <ArrowLeft size={12} /> Zurück zu Events
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1f1f1f] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {event.date_label || formatGermanDate(event.event_at) || 'Neues Event'}
          </h1>
          <p className="text-sm text-gray-500">{event.title}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50">
            <RefreshCw size={14} /> Neu laden
          </button>
          <a href="/ki-masterclass" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50">
            <ExternalLink size={14} /> Landing
          </a>
          <button onClick={deleteEvent} className="inline-flex items-center gap-2 border border-red-300 text-red-600 px-3 py-2 text-xs uppercase tracking-wider hover:bg-red-50">
            <Trash2 size={14} /> Löschen
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Stat label="Status" value={<StatusPill value={event.status} options={STATUS_OPTIONS} />} />
        <Stat label="Max-Plätze" value={event.max_seats || 0} />
        <Stat label="Aktive Anmeldungen" value={active} color="text-[#658a07]" />
        <Stat label="Anwesend" value={attended} />
        <Stat label={`Show-up · ${signups.length || 0} Signups`} value={`${showUp}%`} small />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 mb-4 text-sm">
          <AlertTriangle size={14} className="inline mr-2" /> {error}
        </div>
      )}

      {/* Settings */}
      <div className="border border-gray-200 mb-10">
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-bold text-[#1f1f1f] uppercase text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>Eckdaten &amp; Texte</h2>
          {savedAt && <span className="text-xs text-green-700 flex items-center gap-1"><CheckCircle size={12} /> Gespeichert {savedAt.toLocaleTimeString('de-DE')}</span>}
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Titel" value={event.title} onChange={(v) => updateField('title', v)} />
          <Field label="Untertitel" value={event.subtitle} onChange={(v) => updateField('subtitle', v)} />
          <Field label="Hook-Quote (im Hero)" value={event.hook_quote} onChange={(v) => updateField('hook_quote', v)} textarea />
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5 font-bold">Status</label>
            <select value={event.status || 'draft'} onChange={(e) => updateField('status', e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 border border-gray-200 p-4 bg-amber-50/40">
            <label className="block text-xs uppercase tracking-wider text-gray-600 mb-2 font-bold">
              Exakter Termin (für Auto-Reminder + Sortierung)
            </label>
            <input type="datetime-local"
              value={event.event_at ? toDateTimeLocal(event.event_at) : ''}
              onChange={(e) => updateField('event_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
              className="w-full md:w-auto border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            <p className="text-[11px] text-gray-500 mt-1.5">Optional, aber empfohlen. Wird nicht direkt auf der Landing angezeigt — dafür sind die zwei Text-Felder darunter da.</p>
          </div>
          <Field label='Datum-Label (Anzeige, z.B. "24. Juli 2026")' value={event.date_label} onChange={(v) => updateField('date_label', v)} />
          <Field label='Uhrzeit-Label (z.B. "14:00 – 17:00 Uhr")' value={event.time_label} onChange={(v) => updateField('time_label', v)} />
          <Field label='Format-Label (z.B. "Live online")' value={event.format_label} onChange={(v) => updateField('format_label', v)} />
          <Field label='Ort-Label (optional)' value={event.location_label} onChange={(v) => updateField('location_label', v)} />
          <Field label='Preis-Label (z.B. "249 € zzgl. MwSt.")' value={event.price_label} onChange={(v) => updateField('price_label', v)} />
          <Field label="Preis (€, optional)" value={event.price_eur ?? ''} onChange={(v) => updateField('price_eur', v === '' ? null : parseFloat(v))} type="number" />
          <Field label="Max. Plätze" value={event.max_seats ?? 100} onChange={(v) => updateField('max_seats', parseInt(v, 10) || 0)} type="number" />
          <div className="flex items-center gap-3 pt-6">
            <input type="checkbox" id="signup_open" checked={!!event.signup_open}
              onChange={(e) => updateField('signup_open', e.target.checked)}
              className="w-4 h-4 accent-[#befa0a]" />
            <label htmlFor="signup_open" className="text-sm text-[#1f1f1f]">Anmeldungen aktiv (Formular auf Landing sichtbar)</label>
          </div>
          <Field label="Hero-Bild-URL" value={event.hero_image_url} onChange={(v) => updateField('hero_image_url', v)} />
          <Field label="CTA-Bild-URL" value={event.cta_image_url} onChange={(v) => updateField('cta_image_url', v)} />
          <Field label="Slug (URL-Kennung, z.B. masterclass-juli-2026)" value={event.slug} onChange={(v) => updateField('slug', v)} />
          <Field label="Interne Notizen (nicht öffentlich)" value={event.internal_notes} onChange={(v) => updateField('internal_notes', v)} textarea />
        </div>
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center">
          <span className="text-xs text-gray-400" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Frei: <strong className="text-[#1f1f1f]">{freeSeats}</strong> · ID: <code>{event.id?.slice(0,8)}…</code>
          </span>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-5 py-2.5 text-xs uppercase tracking-wider font-bold hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Save size={14} /> {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Signups */}
      <div className="border border-gray-200">
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center flex-wrap gap-2">
          <h2 className="font-bold text-[#1f1f1f] uppercase text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Anmeldungen ({signups.length})
          </h2>
          <button onClick={() => exportSignupsCSV(signups, event.slug || event.id?.slice(0,8))} disabled={!signups.length}
            className="inline-flex items-center gap-2 border border-gray-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-white disabled:opacity-40">
            <Download size={12} /> CSV
          </button>
        </div>
        {signups.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <UsersIcon size={32} className="mx-auto mb-3 opacity-40" />
            <p>Noch keine Anmeldungen für dieses Event.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-2.5">Eingang</th>
                  <th className="px-4 py-2.5">Name / Unternehmen</th>
                  <th className="px-4 py-2.5">Kontakt</th>
                  <th className="px-4 py-2.5">Quelle</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-center">Da?</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {signups.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      <Clock size={11} className="inline mr-1 opacity-50" />
                      {new Date(s.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#1f1f1f]">{s.name}</div>
                      <div className="text-xs text-gray-500">{s.company || '—'} {s.role ? `· ${s.role}` : ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${s.email}`} className="text-[#658a07] hover:underline text-xs">{s.email}</a>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {s.utm_source || s.referrer_path ? (
                        <>
                          {s.utm_source && <div className="font-mono text-[10px]">utm: {s.utm_source}</div>}
                          {s.referrer_path && <div className="text-[10px]">ref: {s.referrer_path}</div>}
                        </>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <select value={s.status} onChange={(e) => updateSignupField(s.id, 'status', e.target.value)}
                        className="text-xs border border-gray-300 px-2 py-1 bg-white focus:outline-none focus:border-[#befa0a]">
                        {SIGNUP_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input type="checkbox" checked={!!s.attended}
                        onChange={(e) => updateSignupField(s.id, 'attended', e.target.checked)}
                        className="w-4 h-4 accent-[#befa0a]" title="War in der Masterclass anwesend" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteSignup(s.id)} className="text-gray-300 hover:text-red-600 transition-colors" title="Anmeldung löschen">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Helper Components
// ────────────────────────────────────────────────────────────────
function Stat({ label, value, color, small }) {
  return (
    <div className="border border-gray-200 p-4">
      <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</p>
      <p className={`${small ? 'text-lg' : 'text-2xl'} font-bold ${color || 'text-[#1f1f1f]'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>{value}</p>
    </div>
  );
}

function Field({ label, value, onChange, textarea, type }) {
  return (
    <div className={textarea ? 'md:col-span-2' : ''}>
      <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5 font-bold">{label}</label>
      {textarea ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none" />
      ) : (
        <input type={type || 'text'} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      )}
    </div>
  );
}
